import { toZonedTime } from 'date-fns-tz';
import { useCallback, useMemo } from 'react';
import useSWR, { type KeyedMutator, useSWRConfig } from 'swr';
import { backendClient } from '@/lib/api-client';
import { emailDisplayName, isImageFileName, isPowerPointFileName, isSpreadsheetFileName, isTextFileName, isWordFileName, removeTimestampPrefix } from '@/utils/file-utils';


// Duplicate location type for documents that appear in multiple locations
export type DuplicateLocation = {
  guid: string;
  display_name: string;
  folder_name: string;
  folder_path: string;
};

export type AnalysisSetState = 'inherit' | 'included' | 'excluded';
export type AnalysisSetStatus =
  | 'included'
  | 'queued'
  | 'processing'
  | 'current'
  | 'removing'
  | 'removed'
  | 'error';

// Define document type - attachments are also Documents (recursive)
export type Document = {
  id: string; // FileReference GUID - used for all UI operations (delete, select, etc.)
  guid: string; // Document GUID - used for opening files in viewer
  fileName: string;
  displayName?: string;
  date: Date | string;
  from: string[];
  to: string[];
  summary: string;
  tags: string[];
  tag_colors?: Record<string, string>; // Map of tag names to their colors
  attachments: Document[]; // Recursive - attachments are Documents too
  deadlines: { date: Date | string; description: string; deadline_id?: string; state?: string }[];
  fileType?: string[];
  uploadedBy?: string;
  uploadedAt?: string;
  metadata?: any;
  document_type?: {
    id: string;
    guid?: string;
    name: string;
    slug: string;
  };
  is_draft?: boolean;
  is_drafting_managed?: boolean;
  draft_document_guid?: string | null;
  processing_status?: string;
  subject?: string;
  token_count?: number;
  duplicateLocations?: DuplicateLocation[];
  source?: string;
  isAttachment?: boolean;
  bundleSource?: {
    bundle_id: string;
    title: string;
  } | null;
  analysisSetState?: AnalysisSetState;
  analysisSetEffective?: boolean;
  analysisSetStatus?: AnalysisSetStatus | null;
};

// Optional fetch-all enrichment sections. Callers that don't render these can pass them in
// `excludeSections` to skip the backend work and shrink the payload — the document set, names,
// token counts and attachments are unchanged. Mirrors CorrespondenceSection on the backend.
export const DOCUMENT_ENRICHMENT_SECTIONS = [
  'deadlines',
  'tags',
  'duplicate_locations',
  'bundle_source',
  'analysis_set',
] as const;

export type DocumentEnrichmentSection = (typeof DOCUMENT_ENRICHMENT_SECTIONS)[number];

// The key for the same request without `exclude`. Keys that share it return the same documents
// and differ only in which enrichment sections are populated.
export function fullDocumentsKey(key: string): string {
  const [path, query = ''] = key.split('?');
  const params = new URLSearchParams(query);
  params.delete('exclude');
  const rest = params.toString();
  return rest ? `${path}?${rest}` : path;
}

// Per SWR cache: key -> order of the newest request whose documents that key holds or awaits.
// A full response is only copied onto an excluded view when it is newer on both sides, the same
// "newest request wins" rule SWR applies to its own key.
let documentsRequestOrder = 0;
const latestRequestByCache = new WeakMap<object, Map<string, number>>();

function latestRequestByKey(cache: object): Map<string, number> {
  let latest = latestRequestByCache.get(cache);
  if (!latest) {
    latest = new Map();
    latestRequestByCache.set(cache, latest);
  }
  return latest;
}

export type UseDocumentsOptions = {
  analysisSetOnly?: boolean;
  /** Limit results to every folder in the effective Correspondence trees. */
  correspondenceOnly?: boolean;
  /** Enrichment sections to skip (see {@link DOCUMENT_ENRICHMENT_SECTIONS}). */
  excludeSections?: readonly DocumentEnrichmentSection[];
};

// Map raw API response to Document type
const mapToDocument = (data: any, timezone: string = 'UTC'): Document => {
  try {
    // Ensure we have a valid date or fallback to current date
    let uploadedAt = data.metadata?.date;
    if (!uploadedAt || isNaN(new Date(uploadedAt).getTime())) {
      uploadedAt = new Date().toISOString();
    }

    // Create formatted display name for emails
    let displayName = data.filename;
    const isEmail = data.filename.toLowerCase().endsWith('.msg') || data.filename.toLowerCase().endsWith('.eml');
    const isPdf = data.filename.toLowerCase().endsWith('.pdf');
    const isWord = isWordFileName(data.filename);
    const isPowerPoint = isPowerPointFileName(data.filename);
    const isExcel = isSpreadsheetFileName(data.filename);
    const isImage = isImageFileName(data.filename);
    const isText = isTextFileName(data.filename);
    const hasAttachments = Array.isArray(data.attachments) && data.attachments.length > 0;

    if (isEmail) {
      displayName = emailDisplayName(data.filename, data.metadata?.subject) ?? data.filename;
    }

    // Check if this is a bundle document (in Bundles folder)
    const isBundle = data.document_type?.name === 'Bundles';

    if (!isEmail && !isBundle) {
      // Strip timestamp prefix if present (e.g., "20251218_124542_1950_filename.pdf" -> "filename.pdf")
      displayName = removeTimestampPrefix(data.filename);
    }

    // Format email dates to be in the local timezone
    // Format PDFs and other documents to be timezone agnostic
    // Format deadline dates to timezone agnostic
    let date: Date | string;
    if (isEmail) {
      date = toZonedTime(new Date(data.metadata?.date || ''), timezone);
    } else {
      date = data.metadata?.date || '';
    }

    // Recursively map attachments (they are also Documents)
    const attachments: Document[] = Array.isArray(data.attachments)
      ? data.attachments.map((attachment: any) => mapToDocument(attachment, timezone))
      : [];

    return {
      id: data.file_reference_guid, // FileReference GUID - primary ID for UI operations
      guid: data.guid, // Document GUID - for opening files in viewer
      fileName: data.filename,
      displayName,
      date: date,
      from: data.metadata.sender ? data.metadata.sender : [],
      to: data.metadata.recipient ? data.metadata.recipient : [],
      summary: data.metadata.summary || '',
      tags: data.metadata.tags ? data.metadata.tags : [],
      tag_colors: data.tag_colors || data.metadata?.tag_colors || {},
      attachments,
      deadlines: Array.isArray(data.metadata?.deadlines)
        ? data.metadata.deadlines.map((deadline: any) => ({
          date: deadline.date,
          description: deadline.description,
          deadline_id: deadline.deadline_id,
          state: deadline.state
        }))
        : [],
      fileType: [
        isEmail
          ? (hasAttachments ? 'Email with attachments' : 'Email')
          : isPdf
            ? 'PDF'
            : isWord
              ? 'Word'
              : isPowerPoint
                ? 'PowerPoint'
              : isExcel
                ? 'Excel'
                : isImage
                  ? 'Image'
                  : isText
                    ? 'Text'
                    : 'Document'
      ],
      uploadedBy: data.uploaded_by || 'Unknown',
      uploadedAt,
      metadata: data.metadata,
      document_type: data.document_type ? {
        id: data.document_type.id || '',
        guid: data.document_type.guid || undefined,
        name: data.document_type.name || 'Document',
        slug: data.document_type.slug || ''
      } : undefined,
      is_draft: data.is_draft || false,
      is_drafting_managed: data.is_drafting_managed === true,
      draft_document_guid: data.draft_document_guid || null,
      processing_status: data.processing_status || '',
      subject: data.subject || '',
      token_count: data.token_count || 0,
      duplicateLocations: data.duplicate_locations || [],
      source: data.source || 'crimson',
      isAttachment: data.is_attachment || false,
      bundleSource: data.bundle_source || null,
      analysisSetState: data.analysis_set_state || 'inherit',
      analysisSetEffective: data.analysis_set_effective === true,
      analysisSetStatus: data.analysis_set_status || null,
    };
  } catch (err: any) {
    console.error('Error in mapToDocument:', {
      metadata: data.metadata,
      error: err.message,
    });
    throw err;
  }
};


/**
 * Custom hook for fetching document data
 * @param slug The matter slug/guid
 * @param documentTypes Optional list of document types to filter by
 * @param isDraft Optional flag to filter by draft status
 * @param folderIds Optional list of folder IDs to filter by
 * @param config Optional SWR configuration override
 * @param timezone Optional timezone for date formatting (defaults to UTC)
 */
export function useDocuments(
  slug: string | undefined,
  documentTypes?: string[],
  isDraft?: boolean,
  folderIds?: number[],
  config: any = {},
  timezone: string = 'UTC',
  options: UseDocumentsOptions = {}
) {
  // Create query parameters for filtering
  const params = new URLSearchParams();

  if (folderIds?.length) {
    folderIds.forEach((id) => {
      params.append('folders', String(id));
    });
  }

  if (isDraft !== undefined) {
    params.append('is_draft', isDraft.toString());
  }

  if (options.analysisSetOnly) {
    params.append('analysis_set', 'active');
  }

  if (options.correspondenceOnly) {
    params.append('correspondence_sources', 'true');
  }

  for (const section of options.excludeSections ?? []) {
    params.append('exclude', section);
  }

  const queryParams = params.toString() ? `?${params.toString()}` : '';

  // Create a stable key for SWR to prevent double fetching
  const stableSlugKey = useMemo(() =>
    slug && slug !== 'undefined' ? `/correspondence/${slug}/fetch-all${queryParams}` : null,
    [slug, queryParams]
  );

  const { cache, mutate: mutateCache } = useSWRConfig();

  // Define the fetcher function
  const fetcher = async (url: string) => {
    try {
      console.log('Fetching document data from:', url);
      const latest = latestRequestByKey(cache);
      const requestOrder = ++documentsRequestOrder;
      latest.set(url, requestOrder);
      const response = await backendClient.get(url);
      const results: Document[] = response.data.results?.map((item: any) => mapToDocument(item, timezone)) || [];
      if (fullDocumentsKey(url) === url && latest.get(url) === requestOrder) {
        // A full payload is a superset of every excluded view of the same request, so refresh
        // those cached views (e.g. Case Partner's) without fetching them again.
        for (const key of cache.keys()) {
          if (key !== url && fullDocumentsKey(key) === url && (latest.get(key) ?? 0) < requestOrder) {
            latest.set(key, requestOrder);
            void mutateCache(key, results, { revalidate: false });
          }
        }
      }
      return results;
    } catch (err) {
      console.error('Error fetching documents:', err);
      throw err;
    }
  };

  // Use SWR to fetch the data
  const { data, error, mutate, isValidating } = useSWR<Document[]>(
    stableSlugKey,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 5000, // 5 seconds
      ...config
    }
  );

  // Explicitly compute isLoading state
  const isLoading = !error && !data && stableSlugKey !== null;

  // Memoize documents to ensure stable reference when data hasn't changed
  const documents = useMemo(() => data || [], [data]);

  const fullKey = stableSlugKey ? fullDocumentsKey(stableSlugKey) : null;
  const isExcludedView = fullKey !== null && fullKey !== stableSlugKey;
  // A refresh from an excluded view means the documents changed, so screens showing the full
  // payload of the same request must refetch too. Only mounted ones do; others revalidate on mount.
  const mutateWithFullView = useCallback<KeyedMutator<Document[]>>(
    async (...args) => {
      const result = await mutate(...args);
      if (fullKey) {
        void mutateCache(fullKey);
      }
      return result;
    },
    [mutate, mutateCache, fullKey],
  );

  return {
    documents,
    // Undefined until a successful fetch (including empty matters). Citation
    // rendering must use this so an in-flight fetch is not treated as "every
    // cited document was deleted".
    fetchedDocuments: data,
    error,
    isLoading,
    isValidating,
    mutate: isExcludedView ? mutateWithFullView : mutate,
  };
}
